/**
 * Shared instructor/substitute utilities for class-related components
 * Consolidates instructor display logic across:
 * - StudioClassCard
 * - StudioClassTable (CompactClassCard)
 * - ClassCard
 * - BrowseClassCard
 */

export interface ProfileInfo {
  id?: string;
  username?: string | null;
  full_name?: string | null;
  profile_photo?: string | null;
}

export interface InstructorDisplayInfo {
  /** The name to display */
  name: string;
  /** Profile photo URL if available */
  photo: string | null;
  /** Username for linking to profile */
  username: string | null;
  /** Whether this is a substitute instructor */
  isSubstitute: boolean;
  /** Whether the instructor has a profile to link to */
  hasProfile: boolean;
}

/**
 * Get the display information for a class instructor
 * Handles substitute instructors, guest instructors, and regular faculty
 *
 * Priority order:
 * 1. Substitute profile (if exists)
 * 2. Substitute guest name (if exists)
 * 3. Guest instructor name (if exists)
 * 4. Original instructor profile
 *
 * @param profile - The original instructor profile
 * @param guestInstructorName - Name of guest instructor (no profile)
 * @param substituteProfile - Profile of substitute instructor
 * @param substituteGuestName - Name of substitute guest instructor
 * @returns InstructorDisplayInfo with all display details
 */
export function getInstructorDisplayInfo(
  profile?: ProfileInfo | null,
  guestInstructorName?: string | null,
  substituteProfile?: ProfileInfo | null,
  substituteGuestName?: string | null
): InstructorDisplayInfo {
  // Determine the effective instructor in priority order
  // When a guest name is used (no profile), don't link to any profile
  if (substituteProfile) {
    return {
      name: substituteProfile.full_name || substituteProfile.username || "Instructor TBA",
      photo: substituteProfile.profile_photo || null,
      username: substituteProfile.username || null,
      isSubstitute: true,
      hasProfile: true,
    };
  }

  if (substituteGuestName) {
    return {
      name: substituteGuestName,
      photo: null,
      username: null,
      isSubstitute: true,
      hasProfile: false,
    };
  }

  if (guestInstructorName) {
    return {
      name: guestInstructorName,
      photo: null,
      username: null,
      isSubstitute: false,
      hasProfile: false,
    };
  }

  return {
    name: profile?.full_name || profile?.username || "Instructor TBA",
    photo: profile?.profile_photo || null,
    username: profile?.username || null,
    isSubstitute: false,
    hasProfile: !!profile,
  };
}


/**
 * Get the suffix to display for substitute instructors
 * @param isSubstitute - Whether this is a substitute instructor
 * @returns "(Sub)" if substitute, undefined otherwise
 */
export function getSubstituteSuffix(isSubstitute: boolean): string | undefined {
  return isSubstitute ? "(Sub)" : undefined;
}
